import { Home, Users, Building2, Wrench, ChevronLeft, ChevronRight, LogOut, X } from "lucide-react";
import { NavLink as RouterNavLink, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { userHasRole } from "@/lib/rbac";

const allNavItems = [
  { to: "/dashboard", icon: Home, label: "Dashboard", roles: null },
  { to: "/leads", icon: Users, label: "Leads", 
  roles: ["admin", "sales_agent", "operations_user", "app_user", "member"] },
  { to: "/products", icon: Building2, label: "Banks & Products", roles: ["admin", "sales_agent", "operations_user", "app_user", "member"] },
  { to: "/tools", icon: Wrench, label: "Tools", roles: ["admin", "sales_agent", "operations_user", "app_user", "member", "relational_manager"] },
];

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export function Sidebar({ mobileOpen = false, onMobileClose }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      toast.success("Logged out successfully", { description: "You have been logged out of your account." });
      navigate("/");
    } catch (error) {
      toast.error("Logout failed", { description: "An error occurred while logging out." });
    }
  };

  if (!user) return null;

  const navItems = allNavItems.filter(
    (item) => !item.roles || item.roles.some((r) => userHasRole(user, r))
  );

  return (
    <>
      {/* Mobile Overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onMobileClose}
        />
      )}
      
      {/* Sidebar */}
      <aside
        className={cn(
          "flex flex-col fixed left-0 top-0 h-screen h-dvh bg-sidebar text-sidebar-foreground border-r border-sidebar-border transition-all duration-300 z-50",
          collapsed ? "w-[72px]" : "w-64",
          // Desktop: always visible
          "hidden md:flex",
          // Mobile: show when mobileOpen is true
          mobileOpen && "flex"
        )}
        style={{ 
          paddingTop: 'env(safe-area-inset-top)'
        }}
      >
        {/* Close button for mobile */}
        {mobileOpen && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 md:hidden"
            style={{
              top: 'max(1rem, calc(env(safe-area-inset-top) + 1rem))'
            }}
            onClick={onMobileClose}
          >
            <X className="h-5 w-5" />
          </Button>
        )}
      {/* Logo */}
      <div className="flex items-center gap-3 p-4 border-b border-sidebar-border">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sidebar-primary text-sidebar-primary-foreground font-bold text-lg">
          D
        </div>
        {!collapsed && (
          <div className="flex flex-col">
            <span className="font-bold text-lg">DsaLens</span>
            <span className="text-xs text-sidebar-foreground/60">DSA Portal</span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1">
        {navItems.map((item) => (
          <RouterNavLink
            key={item.to}
            to={item.to}
            end={item.to === "/dashboard"}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-all duration-200",
                isActive
                  ? "bg-sidebar-accent text-sidebar-primary"
                  : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
              )
            }
          >
            <item.icon className="h-5 w-5 flex-shrink-0" />
            {!collapsed && <span>{item.label}</span>}
          </RouterNavLink>
        ))}
      </nav>

      {/* User section */}
      <div className="border-t border-sidebar-border p-3 space-y-2">
        {!collapsed && (
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-sidebar-accent text-sm font-semibold">
              {user.name.charAt(0)}
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-medium">{user.name}</span>
              <span className="text-xs text-sidebar-foreground/60 capitalize">
                {(user.primaryRole as string).replace(/_/g, " ")}
              </span>
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <Button
            variant="ghost"
            size={collapsed ? "icon" : "default"}
            className="flex-1 justify-start text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5 mr-2" />}
            {!collapsed && "Collapse"}
          </Button>

          {!collapsed && (
            <Button
              variant="ghost"
              size="icon"
              className="text-sidebar-foreground/70 hover:text-red-600 hover:bg-red-50"
              onClick={handleLogout}
              title="Logout"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          )}
        </div>

        {collapsed && (
          <Button
            variant="ghost"
            size="icon"
            className="w-full text-sidebar-foreground/70 hover:text-red-600 hover:bg-red-50"
            onClick={handleLogout}
            title="Logout"
          >
            <LogOut className="h-5 w-5" />
          </Button>
        )}
      </div>
    </aside>
    </>
  );
}
